import { Avatar, Box, Button, Card, Typography } from '@mui/material';
import { PacoteStatusEnum } from 'enum/pacoteStatus.enum';
import { useNavigate } from 'react-router-dom';

// Icons
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RecyclingIcon from '@mui/icons-material/Recycling';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ArticleIcon from '@mui/icons-material/Article';
import YardIcon from '@mui/icons-material/Yard';
import Groups2Icon from '@mui/icons-material/Groups2';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface IFormulario {
  uuid: string;
  status: number;
  name: string;
  formulario: string;
  hideEditButton?: boolean;
}

const getIconByFormulario = (name: string) => {
  const n = name.toLowerCase();

  if (n.includes('secretaria municipal de agricultura')) return <AccountBalanceIcon sx={{ color: '#fff', fontSize: 36 }} />;
  if (n.includes('conselho municipal de desenvolvimento rural sustentável') || n.includes('cmdrs'))
    return <RecyclingIcon sx={{ color: '#fff', fontSize: 36 }} />;
  if (n.includes('comercialização')) return <CenterFocusStrongIcon sx={{ color: '#fff', fontSize: 36 }} />;
  if (n.includes('política municipal da agricultura familiar')) return <ArticleIcon sx={{ color: '#fff', fontSize: 36 }} />;
  if (n.includes('questões ambientais')) return <YardIcon sx={{ color: '#fff', fontSize: 36 }} />;
  if (n.includes('comunidades rurais') || n.includes('tradicionais')) return <Groups2Icon sx={{ color: '#fff', fontSize: 36 }} />;

  return <DescriptionIcon sx={{ color: '#fff', fontSize: 36 }} />;
}

// Normalizar nomes dos formulários para exibição
const getNormalizedFormularioName = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes('comunidades rurais') && n.includes('tradicionais')) {
    return 'Comunidades';
  }
  return name;
}

export default function CardFormularioStatus({
  uuid,
  status,
  name,
  formulario,
  hideEditButton = false,
}: IFormulario): JSX.Element {
  const navigate = useNavigate();
  const displayName = getNormalizedFormularioName(name);

  const statusConfig = {
    [PacoteStatusEnum.NOVO]: {
      text: 'Novo',
      iconColor: '#0048B6',
      boxColor: '#2B5DC60A',
    },
    [PacoteStatusEnum.RASCUNHO]: {
      text: 'Em Rascunho',
      iconColor: '#0048B6',
      boxColor: '#2B5DC60A',
    },
    [PacoteStatusEnum.CADASTRADO]: {
      text: 'Cadastrado',
      iconColor: '#2E7D32',
      boxColor: '#EAF2EA',
    },
    [PacoteStatusEnum.ENVIADO_PARA_APROVACAO]: {
      text: 'Enviado para Aprovação',
      iconColor: '#ED6C02',
      boxColor: '#ED6C020A',
    },
    /* [PacoteStatusEnum.DEVOLVIDO_PARA_AJUSTE]: {
      text: 'Devolvido para Ajuste',
      iconColor: '#C62828',
      boxColor: '#D32F2F0A',
    }, */
  };

  const statusAtual = statusConfig[status as keyof typeof statusConfig];

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        boxShadow: 3,
        p: 0,
      }}
    >
      {/* Topo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            variant="rounded"
            sx={{ bgcolor: '#0048B6', width: 56, height: 56, mr: 2 }}
          >
            {getIconByFormulario(name)}
          </Avatar>

          <Box>
            <Typography variant="h6" component="div" fontWeight="bold">
              {displayName}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(() => {
            const isEditDisabled = hideEditButton || status === PacoteStatusEnum.ENVIADO_PARA_APROVACAO;
            return (
              <Button
                variant="contained"
                sx={{ backgroundColor: '#0048B6' }}
                size="medium"
                disabled={isEditDisabled}
                onClick={() => {
                  if (!isEditDisabled) navigate(`/pacote-de-formularios/${formulario}/editar/${uuid}`);
                }}
              >
                {status === PacoteStatusEnum.NOVO ? 'Preencher' : 'Editar'}
              </Button>
            );
          })()}

          <Button
            variant="outlined"
            size="medium"
            disabled={status === PacoteStatusEnum.NOVO}
            onClick={() => navigate(`/pacote-de-formularios/${formulario}/visualizar/${uuid}`)}
          >
            Visualizar
          </Button>
        </Box>
      </Box>

      {/* Faixa inferior: Situação */}
      <Box
        sx={{
          bgcolor: statusAtual.boxColor,
          borderTop: '1px solid #E6ECF5',
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <InfoOutlinedIcon sx={{ color: statusAtual.iconColor }} />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          <strong>Situação:</strong>{' '}
          <Typography
            component="span"
            variant="body2"
            sx={{ fontWeight: 600, color: statusAtual.iconColor }}
          >
            {statusAtual.text}
          </Typography>
        </Typography>
      </Box>
    </Card>
  );
}
